import { ValidatorFn } from '@angular/forms';
import * as Num from 'effect/Number';
import * as P from 'effect/Predicate';

interface MaxBlobSizeValidatorArgs {
  /** Maximum allowed size in bytes. */
  max: number;
  /** If `true`, the maximum is exclusive meaning that validation fails at the same size. */
  exclusive: boolean;
}

export interface MaxSizeValidationError {
  /** Maximum allowed size in bytes. */
  max: number;
  /** Actual size in bytes. */
  actual: number;
}

const MAX_SIZE_VALIDATOR_ERROR_PROP_NAME = 'maxBlobSize';

const _hasSizeProperty = P.and(P.isNotNullable, P.struct({ size: P.isNumber }));
const _isMaxSizeValidationError: P.Refinement<unknown, MaxSizeValidationError> =
  P.compose(
    P.and(P.hasProperty('actual'), P.hasProperty('max')),
    P.struct({
      actual: P.isNumber,
      max: P.isNumber,
    }),
  );

/**
 * Type guard for checking for a max blob size validation error.
 */
export const hasMaxBlobSizeError = P.compose(
  P.hasProperty(MAX_SIZE_VALIDATOR_ERROR_PROP_NAME),
  P.struct({
    [MAX_SIZE_VALIDATOR_ERROR_PROP_NAME]: _isMaxSizeValidationError,
  }),
);

/**
 * Creates a validation function that tests if the size of a
 * blob does not exceed a certain maximum.
 *
 * @param param0 Validator arguments
 *
 * @returns
 * Validator for maximum blob size.
 */
export function maxBlobSize({
  max,
  exclusive,
}: MaxBlobSizeValidatorArgs): ValidatorFn {
  const cmpFn = exclusive
    ? Num.greaterThanOrEqualTo(max)
    : Num.greaterThan(max);

  return (
    control,
  ): Record<
    typeof MAX_SIZE_VALIDATOR_ERROR_PROP_NAME,
    MaxSizeValidationError
  > | null => {
    const { value } = control;

    if (!_hasSizeProperty(value)) {
      return null;
    }

    const actual = value.size;
    return cmpFn(actual)
      ? { [MAX_SIZE_VALIDATOR_ERROR_PROP_NAME]: { actual, max } }
      : null;
  };
}
